import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

interface Logo {
  id: string;
  url: string;
  prompt: string;
}

interface LogoApiResponse {
  logos: Logo[];
  success?: boolean;
  warning?: string;
  error?: string;
}

const LogoGenerator: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  const [style, setStyle] = useState('');
  const [colors, setColors] = useState<string[]>([]);
  const [colorInput, setColorInput] = useState('');
  const [logos, setLogos] = useState<Logo[]>([]);
  const [isPlaceholder, setIsPlaceholder] = useState(false);
  const [apiWarning, setApiWarning] = useState<string | null>(null);
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleAddColor = () => {
    if (colorInput.trim() && !colors.includes(colorInput.trim())) {
      setColors([...colors, colorInput.trim()]);
      setColorInput('');
    }
  };

  const handleRemoveColor = (color: string) => {
    setColors(colors.filter(c => c !== color));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddColor();
    }
  };

  // Handle downloading a logo image
  const handleDownload = useCallback(async (url: string, index: number) => {
    try {
      setDownloadingIndex(index);
      
      // Fetch the image
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Create a safe filename
      const filename = `${businessName.replace(/\W+/g, '-').toLowerCase()}-logo-${index + 1}.png`;
      
      // Save the file
      saveAs(blob, filename);
      toast.success('Logo downloaded successfully!');
    } catch (error) {
      console.error('Error downloading logo:', error);
      toast.error('Failed to download logo. Please try again.');
    } finally {
      setDownloadingIndex(null);
    }
  }, [businessName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!businessName || !industry || !style || colors.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setApiWarning(null);
    setIsPlaceholder(false);
    
    try {
      // Show generating toast that will be dismissed when complete
      const generatingToast = toast.loading('Generating logos...');
      
      const userId = await getToken();
      
      // Check if userId is available
      if (!userId) {
        toast.error('Authentication error. Please try logging in again.');
        return;
      }
      
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/logo`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            userId,
            businessName, 
            industry, 
            style, 
            colors 
          }),
        }
      );
      
      const data: LogoApiResponse = await response.json();
      
      // Dismiss the loading toast
      toast.dismiss(generatingToast);
      
      if (data.logos && data.logos.length > 0) {
        setLogos(data.logos);
        
        // Check if these are placeholder logos
        if (data.warning) {
          setIsPlaceholder(true);
          setApiWarning(data.warning);
          toast.success('Logo mockups created!', { 
            icon: '⚠️',
            duration: 5000
          });
        } else {
          setIsPlaceholder(false);
          toast.success('Logos generated successfully!');
        }
      } else {
        throw new Error(data.error || 'No logos were returned');
      }
    } catch (error) {
      console.error('Error generating logos:', error);
      toast.error('Failed to generate logos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-10 relative overflow-hidden">
      {/* Decorative blurred orbs */}
      <div className="absolute top-40 -left-24 w-72 h-72 bg-gradient-to-br from-purple-200 to-blue-300 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute -top-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-300 rounded-full opacity-20 blur-3xl"></div>
      
      <div className="relative z-10 mb-10">
        <h1 className="text-4xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Logo Generator</h1>
        <p className="text-gray-700 text-lg max-w-3xl">
          Create professional logos for your business with customizable styles and colors.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
          
          <div className="relative z-10">
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-6">Generate Logo</h2>
            
            <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="businessName" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                Business Name <span className="text-blue-500">*</span>
              </label>
              <input
                type="text"
                id="businessName"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g. Acme Solutions"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="industry" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                Industry <span className="text-blue-500">*</span>
              </label>
              <input
                type="text"
                id="industry"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Technology, Food, Healthcare"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="style" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                Logo Style <span className="text-blue-500">*</span>
              </label>
              <select
                id="style"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                required
              >
                <option value="">Select a style</option>
                <option value="minimalist">Minimalist</option>
                <option value="modern">Modern</option>
                <option value="vintage">Vintage</option>
                <option value="abstract">Abstract</option>
                <option value="geometric">Geometric</option>
                <option value="mascot">Mascot</option>
                <option value="wordmark">Wordmark</option>
                <option value="emblem">Emblem</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label htmlFor="colors" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                Colors <span className="text-blue-500">*</span>
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="colors"
                  className="flex-grow px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-l-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                  value={colorInput}
                  onChange={(e) => setColorInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="e.g. blue, red, green"
                />
                <button
                  type="button"
                  onClick={handleAddColor}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-3 rounded-r-xl hover:from-blue-600 hover:to-purple-600 shadow-sm hover:shadow transition-all duration-200"
                >
                  Add
                </button>
              </div>
              
              {colors.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {colors.map((color, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/30 backdrop-blur-sm border border-white/40 shadow-sm text-indigo-800"
                      style={{
                        background: `linear-gradient(to right, rgba(79, 70, 229, 0.05), rgba(147, 51, 234, 0.05))`,
                        boxShadow: `0 2px 6px rgba(79, 70, 229, 0.05)`
                      }}
                    >
                      {color}
                      <button
                        type="button"
                        className="ml-1.5 inline-flex text-indigo-500 hover:text-indigo-700 transition-colors focus:outline-none"
                        onClick={() => handleRemoveColor(color)}
                      >
                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-6 border border-transparent rounded-xl shadow-lg text-white font-medium text-base bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200 transform hover:scale-[1.02] ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </div>
              ) : (
                'Generate Logos'
              )}
            </button>
          </form>
          </div>
        </div>
        
        <div>
          {logos.length > 0 ? (
            <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8 overflow-hidden">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
              
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-6">Generated Logos</h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {logos.map((logo, index) => (
                    <div
                      key={logo.id}
                      className="group relative backdrop-blur-sm bg-white/40 border border-white/40 rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 to-purple-100/10 opacity-40"></div>
                      <div className="relative">
                        <img
                          src={logo.url}
                          alt={`Logo for ${businessName}`}
                          className="w-full h-auto"
                        />
                        <div className="p-4 backdrop-blur-sm bg-white/30">
                          <button
                            className="w-full py-2 px-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg text-sm font-medium hover:from-blue-600 hover:to-purple-600 transition-all duration-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-70 disabled:cursor-not-allowed"
                            onClick={() => handleDownload(logo.url, index)}
                            disabled={downloadingIndex === index}
                          >
                            {downloadingIndex === index ? (
                              <div className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Downloading...
                              </div>
                            ) : 'Download'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 backdrop-blur-sm bg-white/30 border border-white/30 rounded-xl">
                  <h3 className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">Prompt Used:</h3>
                  <p className="text-sm text-gray-600">{logos[0]?.prompt}</p>
                  
                  {isPlaceholder && apiWarning && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-amber-800">Notice</h3>
                          <div className="mt-1 text-sm text-amber-700">
                            <p>{apiWarning}</p>
                            <p className="mt-2">These are placeholder mockups. For production-quality logos, please ensure the Ideogram API is properly configured.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-lg p-10 text-center overflow-hidden">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
              
              <div className="relative z-10">
                <div className="mx-auto w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-purple-100 opacity-60">
                  <svg className="w-10 h-10 text-indigo-500 opacity-80" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No logos generated yet</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Fill out the form and click "Generate Logos" to create custom logos for your business.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="relative mt-10 overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-lg p-8">
        {/* Decorative blurred orbs */}
        <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-60 h-60 bg-gradient-to-br from-purple-200 to-blue-200 rounded-full opacity-20 blur-3xl"></div>
        
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">How It Works</h2>
          <p className="mb-6 text-gray-700">
            Our Logo Generator uses advanced AI to create professional logos based on your business name, industry, and preferred style and colors.
          </p>
          
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Tips for Better Results:</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            <li className="flex items-center text-gray-700">
              <div className="flex-shrink-0 mr-2 w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" fillRule="evenodd"></path></svg>
              </div>
              Choose colors that reflect your brand personality
            </li>
            <li className="flex items-center text-gray-700">
              <div className="flex-shrink-0 mr-2 w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" fillRule="evenodd"></path></svg>
              </div>
              Select a style that matches your industry standards
            </li>
            <li className="flex items-center text-gray-700">
              <div className="flex-shrink-0 mr-2 w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" fillRule="evenodd"></path></svg>
              </div>
              Be specific about your industry for more relevant designs
            </li>
            <li className="flex items-center text-gray-700">
              <div className="flex-shrink-0 mr-2 w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" fillRule="evenodd"></path></svg>
              </div>
              Use a clear and concise business name
            </li>
            <li className="flex items-center text-gray-700">
              <div className="flex-shrink-0 mr-2 w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" fillRule="evenodd"></path></svg>
              </div>
              Try different combinations if you're not satisfied with the results
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LogoGenerator;
